import { SharedService } from 'src/app/shared/services/shared-service/shared.service';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, NgZone, OnInit, ViewChild } from '@angular/core';
import { environment } from './core/environments/environment';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import {
  DialogV2Module,
  SnackbarV3Message,
  SnackbarV3Component,
  SnackbarV3Service,
} from '@gea/digital-ui-lib';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { AppInitService } from './shared/services/shared-service/app-init.service';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { DatabaseService } from './features/database/components/services/database-service';
import { first } from 'rxjs';
import { SignalRService } from './shared/services/shared-service/signalr.service';
import { AuthService } from './core/services/auth.service';
import { DxLoadPanelModule } from 'devextreme-angular';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    RouterOutlet,
    CommonModule,
    SnackbarV3Component,
    DialogV2Module,
    ConfirmDialogModule,
    ProgressSpinnerModule,
    DxLoadPanelModule,
  ],
  providers: [ConfirmationService, SharedService],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent implements OnInit {
  public databaseConnectivitySetting: string;
  public isInitialized = false;
  private isInitializing = false;
  public isPublicRoute = false;
  private readonly publicRoutes = ['/login', '/change-password', '/manuals', '/about'];
  public scriptLogs: string[] = [];
  public isDialogOpen = false;
  public isLoading = false;

  @ViewChild('logContainer') logContainer!: ElementRef;
  constructor(
    private readonly http: HttpClient,
    private readonly router: Router,
    private readonly databaseService: DatabaseService,
    private readonly appInitService: AppInitService,
    private readonly confirmationService: ConfirmationService,
    private readonly SnackbarV3Service: SnackbarV3Service,
    private readonly signalRService: SignalRService,
    private readonly ngZone: NgZone,
    private readonly cdr: ChangeDetectorRef,
    private readonly authService: AuthService,
    private readonly sharedService: SharedService,
  ) {
    this.databaseConnectivitySetting = '';
  }

  ngOnInit() {
    this.updateRouteState(this.router.url);
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.updateRouteState(event.urlAfterRedirects);
        this.reloadContent();
      }
    });

    if (environment.name.includes('cloud')) {
      this.databaseService.isConnected$.next(true);
      // Wait for tokens to be ready before showing content
      this.appInitService.tokensReady$.subscribe({
        next: (ready) => {
          if (ready) {
            this.isInitialized = true;
            this.getDatabaseConnectivitySetting();
            this.reloadContent();
          }
        }
      });
    } else {
      // For standalone, wait for authentication before initializing
      this.authService.session$.subscribe({
        next: (session) => {
          if (session && !this.isInitialized) {
            this.initializeApp();
          }
        }
      });
    }

    this.getDatabaseConnectivitySetting();
  }

  private updateRouteState(url: string): void {
    this.isPublicRoute = this.publicRoutes.some(route => url.startsWith(route));
    this.reloadContent();
  }

  private initializeApp(): void {
    if (this.isInitializing || this.isInitialized) {
      return;
    }
    this.isInitializing = true;
    const isResetConnection = sessionStorage.getItem('isResetConnection');
    
    // Set initial connection status to true to allow navigation to proceed
    // verifyDatabaseVersion will handle redirection if it's actually not connected
    this.databaseService.isConnected$.next(true);

    if (!isResetConnection || isResetConnection === undefined) {
      this.databaseService.resetConnection().subscribe({
        next: () => {
          sessionStorage.setItem('isResetConnection', "true");
          this.callAfterReset();
          this.isInitializing = false;
        },
        error: (err) => {
          console.error("Reset connection failed", err);
          this.isInitialized = true;
          this.isInitializing = false;
          this.reloadContent();
        }
      });
    } else {
      this.callAfterReset();
      this.isInitializing = false;
    }
  }

  private callAfterReset() {
    this.appInitService.setTokenStatus(true);
    this.isInitialized = true;    
    this.getDatabaseConnectivitySetting();
    this.verifyDatabaseVersion();
    if(!environment.name.includes('cloud')) {
      this.databaseService.getConnectedDatabase().pipe(first()).subscribe((databaseDetail) => {
          this.databaseService.connectDatabase = {'name': databaseDetail.database, 'server': databaseDetail.server};
      });
    }
    this.reloadContent();
  }

  private reloadContent(): void {
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  public getBaseUrl(url: string): string {
    return environment.api.url + url;
  }

  getDatabaseConnectivitySetting() {
    this.http
      .get<boolean>(this.getBaseUrl('/System/IsRunningOnSqlite'))
      .subscribe({
        next: (result: boolean) => {
          this.databaseConnectivitySetting = result ? 'Sqlite' : 'Sql Server';
        },
        error: (error) => {
          console.log('Error loading areas');
          console.error(error);
        }
      });
  }

    // Block slash key press
  @HostListener('document:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    if (event.key === '/') {
      const target = event.target as HTMLElement;      
      // bypass if element explicitly allows slash
      if (target?.closest('.data-allow-slash')) {
        return;
      } 
      event.preventDefault();
      event.stopPropagation();
    }
  }

  // Handle paste globally
  @HostListener('document:paste', ['$event'])
  onPaste(event: ClipboardEvent) {
    const clipboardData = event.clipboardData;
    if (!clipboardData) return;

    const pastedText = clipboardData.getData('text');

    if (pastedText.includes('/')) {
      const target = event.target as HTMLElement;

      if (target?.closest('.data-allow-slash')) {
        return;
      } 
      event.preventDefault();
      const cleanedText = pastedText.replace(/\//g, '');

      // Insert cleaned text at cursor position
      if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
        const start = target.selectionStart ?? 0;
        const end = target.selectionEnd ?? 0;

        target.value =
          target.value.substring(0, start) +
          cleanedText +
          target.value.substring(end);

        // Move cursor
        const newCursorPos = start + cleanedText.length;
        target.setSelectionRange(newCursorPos, newCursorPos);

        // Trigger Angular change detection
        target.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
  }

  public async verifyDatabaseVersion() {
    this.databaseService.verifyDatabaseVersion().subscribe({
      next: (result: any) => {
        if (!result.isUpdated && result?.isCofirmPromt) {
          const message = `<div class="confirmation-dialog"><p class="device-message">${result?.message}</p></div>`;

          this.confirmationService.confirm({
            header: `Update Required`,
            message: `<div>${message}</div>`,
            acceptLabel: `Confirm`,
            rejectLabel: 'Cancel',
            accept: () => {
              this.confirmCallback();
            },
            reject: () => {
              this.confirmationService.close();
              this.databaseService.isConnected$.next(false);
            },
            closeOnEscape: true,
            key: 'verifyDatabaseVersionDialog',
            defaultFocus: 'none',
          });
        } else if (
          !result.isUpdated &&
          !result?.isCofirmPromt &&
          result.isNoDatabase
        ) {
          this.databaseService.isConnected$.next(false);
          this.router.navigate(['/database/open-database']);
        } else {
          this.databaseService.isConnected$.next(true);
        }
      },
      error: (error) => {
        if (error.error) {
          this.databaseService.isConnected$.next(false);
          const message: SnackbarV3Message = {
            detail: error.error,
            summary: 'Error',
            severity: 'error',
          };
          this.SnackbarV3Service.add(message);
          this.router.navigate(['/database/open-database']);
        }
        console.log('Error loading areas');
        console.error(error);
      }
    });
  }

  public async confirmCallback() {
    try {
      this.isLoading = true;
      const connectionId = await this.signalRService.startConnection();
      this.signalRService.onMessage((chunk: string) => {
        this.ngZone.run(() => {
          if (!this.isDialogOpen) {
            this.isLoading = false;
            this.scriptLogs = [];
            this.renderStreamingDialog();
            this.isDialogOpen = true;
          }
          this.scriptLogs = [...this.scriptLogs, chunk];
          this.cdr.detectChanges();
          this.scrollToBottom();
        });
      });

      this.databaseService.executeScriptPost(connectionId).subscribe({
        next: (response: any) => {
          this.sharedService.showCompletedDialog('scriptStreamingDialog')
         let message: SnackbarV3Message = {
          detail: response?.message || 'Operation completed successfully',
          summary: 'Success',
          severity: 'success',
        };        
        // Handle known non-error info messages
        if (response?.message?.includes('No changes')) {
          message = {
            detail: response.message,
            summary: 'Info',
            severity: 'info',
          };
        }

        // Handle business logic errors returned with 200
        if (
          response?.message &&
          (response.message.includes('Error') ||
            response.message.includes('Bad Request'))
        ) {
          message = {
            detail: response.message,
            summary: 'Error',
            severity: 'error',
          };
        }
        if (response?.logFile) {
            const blob = this.base64ToBlob(response.logFile, 'text/plain');
            this.downloadBlob(blob, `DbScriptExecution.log`);
          }
          this.databaseService.isConnected$.next(true);
          this.SnackbarV3Service.add(message);
        },
        error: (error: any) => {
           this.sharedService.showCompletedDialog('scriptStreamingDialog')
          console.error(error);
             this.databaseService.isConnected$.next(false);
             const detail =
               error?.error?.message ??
               (typeof error?.error === 'string' ? error.error : null) ??
                  error?.message ??
                  'Failed to execute script';

             if (error?.error?.logFile) {
               const blob = this.base64ToBlob(error.error.logFile, 'text/plain');
               this.downloadBlob(blob, `DbScriptExecution.log`);
             }
           
             this.ngZone.run(() => {
               this.SnackbarV3Service.add({
                 detail,
                 summary: 'Error',
                 severity: 'error',
               });
             });
        },
      });
    } catch (error) {
      console.error('SignalR connection failed:', error);
    }
  }

  private renderStreamingDialog() {
    if (!this.isDialogOpen) {
      this.isDialogOpen = true;

      this.confirmationService.confirm({
        header: 'Executing Script',
        rejectVisible: false,
        acceptVisible: false,
        closeOnEscape: false,
        closable: false,
        key: 'scriptStreamingDialog',
        defaultFocus: 'none',
        accept: () => {
          this.confirmationService.close();
          this.isDialogOpen = false;
        },
      });
    }
  }

  private scrollToBottom() {
    if (this.logContainer?.nativeElement) {
      const el = this.logContainer.nativeElement;
      el.scrollTop = el.scrollHeight;
    }
  }

  private downloadBlob(blob: Blob, fileName: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  }
  
  private base64ToBlob(base64: string, mimeType: string) {
    const byteCharacters = this.decodeBase64(base64);
    const byteNumbers = Array.from(byteCharacters, char => char.charCodeAt(0));
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
  }

  private decodeBase64(base64: string): string {
    return atob(base64);
  }

  handleResponseMessage(response: any) {
    let validationMessage = null;
    let message: SnackbarV3Message = {
      detail: response.message || 'An unknown error occurred',
      summary: 'Success',
      severity: 'success',
    };
    if (response instanceof HttpErrorResponse) {
      if (response.error) {
        // Check if the error is a string message (like "No changes to save.")
        if (typeof response.error === 'string') {
          validationMessage = response.error;
        }
        // Check if the error contains validation messages
        else if (response.error.errors) {
          const validationErrors = Object.values(response.error.errors).flat();
          validationMessage = validationErrors.join(', ');
        }
      }
    } else if (response instanceof Error) {
      validationMessage = response.message || 'An unknown error occurred';
    }
    // Update message based on error or validation message
    if (
      validationMessage ||
      (response.message?.includes('Error') ||
        response.message?.includes('Bad Request'))
    ) {
      message = {
        detail: validationMessage || response.message,
        summary: 'Error',
        severity: 'error',
      };
    } else if (
      validationMessage ||
      response.message?.includes('No changes')
    ) {
      message = {
        detail: validationMessage || response.message,
        summary: 'Info',
        severity: 'info',
      };
    }
    this.SnackbarV3Service.add(message);
  }
}
